import axios from "axios";

/**
 * Backend base URL resolution.
 *
 * Why this is more than a one-liner: Emergent's deployment pipeline
 * baked the wrong value into `process.env.REACT_APP_BACKEND_URL` on
 * multiple production builds — it kept resolving to the internal
 * cluster URL (`kodesh-learn.cluster-2.deploy.emergentcf.cloud`)
 * instead of the public domain (`https://torahya.com`). Every fix in
 * Emergent's Secrets / System Keys UI got reverted on the next deploy,
 * so users hit Cloudflare 520s with `/api/*` requests pointed at the
 * wrong host.
 *
 * Defensive resolution: trust `REACT_APP_BACKEND_URL` ONLY if its host
 * matches `window.location.host`. Otherwise fall back to the page's
 * own origin — which by Emergent's architecture ALWAYS serves both the
 * frontend AND the `/api/*` routes from the same host (the
 * Kubernetes ingress routes `/api` to backend:8001 and everything else
 * to frontend:3000).
 *
 * Same-origin fallback is safe in every Emergent environment we
 * support (preview, custom domain, deploy.emergentcf.cloud).
 *
 * SSR / build-time: `window` is undefined, so we just emit whatever
 * REACT_APP_BACKEND_URL holds. The runtime check kicks in on first
 * import in the browser.
 */
const ENV_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

function resolveBackendUrl() {
  if (typeof window === "undefined") return ENV_BACKEND_URL;
  if (ENV_BACKEND_URL) {
    try {
      const u = new URL(ENV_BACKEND_URL);
      // ALSO accept if the env URL is just a path-less absolute origin
      // that resolves to our host. Use `host` (includes port) so we
      // don't accidentally accept a different port on the same domain.
      if (u.host === window.location.host) return ENV_BACKEND_URL.replace(/\/$/, "");
    } catch {
      /* malformed REACT_APP_BACKEND_URL — fall through */
    }
  }
  // Fallback — current origin. Strip trailing slash to keep API
  // template strings (`${API}/auth/...`) tidy.
  return window.location.origin.replace(/\/$/, "");
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export { client };

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach token when available
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("tanakh_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default client;

// ---- API helpers ----
export const api = {
  // auth
  register: (payload) => client.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => client.post("/auth/login", payload).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  completeOnboarding: () => client.post("/me/onboarding-complete").then((r) => r.data),
  weeklyCheckin: () => client.post("/me/checkin").then((r) => r.data),
  snoozeWeeklyCheckin: () => client.post("/me/checkin/snooze").then((r) => r.data),

  // basket — per-user, persisted server-side
  getBasket: () => client.get("/me/basket").then((r) => r.data),
  saveBasket: (items) => client.put("/me/basket", { items }).then((r) => r.data),

  // tanakh
  books: () => client.get("/books").then((r) => r.data),
  book: (slug) => client.get(`/books/${encodeURIComponent(slug)}`).then((r) => r.data),
  chapter: (slug, ch) => client.get(`/chapter/${encodeURIComponent(slug)}/${ch}`).then((r) => r.data),
  verse: (slug, ch, vs) => client.get(`/verse/${encodeURIComponent(slug)}/${ch}/${vs}`).then((r) => r.data),
  commentaries: (slug, ch, vs, list) =>
    client
      .get(`/commentaries/${encodeURIComponent(slug)}/${ch}/${vs}`, {
        params: list ? { commentators: list.join(",") } : {},
      })
      .then((r) => r.data),
  search: (q) => client.get("/search", { params: { q } }).then((r) => r.data),
  searchPersonal: (q) => client.get("/search/personal", { params: { q } }).then((r) => r.data),
  dailyVerse: () => client.get("/daily-verse").then((r) => r.data),
  commentators: () => client.get("/commentators").then((r) => r.data),

  // siddur — public prayer book endpoints
  siddurShacharitAshkenaz: () =>
    client.get("/siddur/ashkenaz/shacharit").then((r) => r.data),
  siddurTachanunStatus: (date) =>
    client
      .get("/siddur/tachanun-status", { params: date ? { date } : {} })
      .then((r) => r.data),

  // chavruta — strict-RAG study partner
  chavrutaQuota: () => client.get("/chavruta/quota").then((r) => r.data),
  chavrutaAvailability: () => {
    // Debug knob — `window.__torahyaShabbatAt = "2026-05-30T12:00:00+03:00"`
    // simulates "what's the chavruta status at that moment". Used by
    // QA + admin debugging only; not surfaced in UI.
    const at = (typeof window !== "undefined" && window.__torahyaShabbatAt) || "";
    const q = at ? `?at=${encodeURIComponent(at)}` : "";
    return client.get(`/chavruta/availability${q}`).then((r) => r.data);
  },
  chavrutaAsk: (payload) => client.post("/chavruta/ask", payload).then((r) => r.data),
  chavrutaThreads: () => client.get("/chavruta/threads").then((r) => r.data),
  chavrutaThread: (id) => client.get(`/chavruta/threads/${id}`).then((r) => r.data),
  // "שפוך את הלב" — pour-out-your-heart space.
  heartQuota: () => client.get("/heart/quota").then((r) => r.data),
  heartEntries: (limit = 50) =>
    client.get(`/heart/entries?limit=${limit}`).then((r) => r.data),
  heartPour: (text, lang) =>
    client.post("/heart/pour", { text, lang }).then((r) => r.data),
  heartDeleteEntry: (id) =>
    client.delete(`/heart/${encodeURIComponent(id)}`).then((r) => r.data),
  heartDeleteAll: () =>
    client.delete("/heart/all").then((r) => r.data),
  setUserGender: (gender) => client.put("/me/gender", { gender }).then((r) => r.data),

  // parasha
  parashaCurrent: () => client.get("/parasha/current").then((r) => r.data),
  parashaCurrentInsight: (lang) => client.get("/parasha/current/insight", { params: lang ? { lang } : undefined }).then((r) => r.data),
  parashaList: () => client.get("/parasha").then((r) => r.data),
  parashaDetail: (slug) => client.get(`/parasha/${encodeURIComponent(slug)}`).then((r) => r.data),
  parashaAggadah: (slug, lang) => client.get(`/parasha/${encodeURIComponent(slug)}/aggadah`, { params: lang ? { lang } : undefined }).then((r) => r.data),
  parashaStory:   (slug, lang) => client.get(`/parasha/${encodeURIComponent(slug)}/story`,   { params: lang ? { lang } : undefined }).then((r) => r.data),
  // Admin-only coverage report — used by /admin/aggadot preview page.
  adminAggadotCoverage: () => client.get("/admin/aggadot/coverage").then((r) => r.data),
  // Admin-only: fetch the EN variant of an aggadah/story for review.
  // `kind` = "aggadah" | "story". Triggers LLM generation if not cached.
  adminAggadotEn: (slug, kind) =>
    client.get(`/admin/aggadot/${encodeURIComponent(slug)}/en`, { params: { kind: kind || "aggadah" } }).then((r) => r.data),
  // Admin-only: toggle the EN approval flag.
  adminAggadotApproveEn: (slug, kind, approved) =>
    client.post(`/admin/aggadot/${encodeURIComponent(slug)}/approve-en`, { kind: kind || "aggadah", approved: !!approved }).then((r) => r.data),
  // Same as parashaDetail() but reshaped to match parashaCurrent()'s
  // payload — adds `opening_verse: {he, en, ref}` from the embedded
  // chapter. Lets the Landing card render any parasha (prev / next
  // browsing) without a second roundtrip.
  parashaCardBySlug: (slug) =>
    client.get(`/parasha/${encodeURIComponent(slug)}`).then((r) => {
      const d = r.data || {};
      const verses = d.chapter?.verses || [];
      const idx = ((d.start?.[1] || 1) - 1);
      const v = verses[idx] || {};
      return {
        ...d,
        opening_verse: {
          he:  v.he  || "",
          en:  v.en  || "",
          ref: `${d.book} ${d.start?.[0]}:${d.start?.[1]}`,
        },
      };
    }),
  parashaAddToBasket: (slug) =>
    client.post("/lessons/parasha-add-to-basket", slug ? { slug } : {}).then((r) => r.data),

  // BYOK — per-user encrypted API keys
  listApiKeys: () => client.get("/me/api-keys").then((r) => r.data),
  setApiKey: (provider, apiKey) =>
    client
      .post(`/me/api-keys/${encodeURIComponent(provider)}`, { api_key: apiKey })
      .then((r) => r.data),
  validateApiKey: (provider) =>
    client
      .post(`/me/api-keys/${encodeURIComponent(provider)}/validate`)
      .then((r) => r.data),
  deleteApiKey: (provider) =>
    client.delete(`/me/api-keys/${encodeURIComponent(provider)}`).then((r) => r.data),

  // google auth — pass optional referral_code so referrals are
  // attributed even when the new user signs in with Google.
  googleSession: (session_id, referral_code) =>
    client
      .post("/auth/google/session", { session_id, referral_code })
      .then((r) => r.data),

  // Custom Google OAuth — Authorization Code flow. Public client_id
  // is fetched from the BE so it isn't baked into the bundle.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH. The frontend always uses
  // `${window.location.origin}/auth/google` and the BE echoes it
  // verbatim to Google during the token exchange.
  googleOAuthConfig: () =>
    client.get("/auth/google/config").then((r) => r.data),
  googleOAuthExchange: ({ code, redirect_uri, referral_code }) =>
    client
      .post("/auth/google/oauth", { code, redirect_uri, referral_code })
      .then((r) => r.data),

  // user features
  bookmarks: () => client.get("/bookmarks").then((r) => r.data),
  addBookmark: (p) => client.post("/bookmarks", p).then((r) => r.data),
  delBookmark: (id) => client.delete(`/bookmarks/${id}`).then((r) => r.data),

  // List notes — pass {book, chapter} to filter, omit for all
  notes: (params) => client.get("/notes", { params }).then((r) => r.data),
  addNote: (p) => client.post("/notes", p).then((r) => r.data),
  updateNote: (id, text) => client.put(`/notes/${id}`, { text }).then((r) => r.data),
  delNote: (id) => client.delete(`/notes/${id}`).then((r) => r.data),

  // List highlights — pass {book, chapter} to filter, omit for all
  highlights: (params) => client.get("/highlights", { params }).then((r) => r.data),
  addHighlight: (p) => client.post("/highlights", p).then((r) => r.data),
  delHighlight: (id) => client.delete(`/highlights/${id}`).then((r) => r.data),

  getProgress: () => client.get("/progress").then((r) => r.data),
  setProgress: (p) => client.post("/progress", p).then((r) => r.data),

  aiExplain: (p) => client.post("/ai/explain", p).then((r) => r.data),

  // Torah lesson builder
  buildLesson: (p) => client.post("/lessons/build", p).then((r) => r.data),
  lessonJobStatus: (jobId) =>
    client.get(`/lessons/jobs/${encodeURIComponent(jobId)}`).then((r) => r.data),
  whoSaysWhat: (p) => client.post("/lessons/who-says-what", p).then((r) => r.data),

  // Saved lesson library
  saveLesson:  (p)  => client.post("/lessons", p).then((r) => r.data),
  listLessons: ()   => client.get("/lessons").then((r) => r.data),
  getLesson:   (id) => client.get(`/lessons/${id}`).then((r) => r.data),
  updateLesson:(id, payload) => client.put(`/lessons/${id}`, payload).then((r) => r.data),
  delLesson:   (id) => client.delete(`/lessons/${id}`).then((r) => r.data),

  // Beit Midrash workspace
  meDashboard: () => client.get("/me/dashboard").then((r) => r.data),

  // Notebook (המחברת החכמה)
  notebookSections: () => client.get("/notebook/sections").then((r) => r.data),
  notebookCreateSection: (p) => client.post("/notebook/sections", p).then((r) => r.data),
  notebookUpdateSection: (id, p) => client.patch(`/notebook/sections/${id}`, p).then((r) => r.data),
  notebookDeleteSection: (id) => client.delete(`/notebook/sections/${id}`).then((r) => r.data),
  notebookEntries: (params) => client.get("/notebook/entries", { params }).then((r) => r.data),
  notebookCreateEntry: (p) => client.post("/notebook/entries", p).then((r) => r.data),
  notebookUpdateEntry: (id, p) => client.patch(`/notebook/entries/${id}`, p).then((r) => r.data),
  notebookDeleteEntry: (id) => client.delete(`/notebook/entries/${id}`).then((r) => r.data),
  notebookStats: () => client.get("/notebook/stats").then((r) => r.data),

  // Donations
  donationCauses: () => client.get("/donations/causes").then((r) => r.data),
  createDonationCheckout: (payload, originUrl) =>
    client
      .post("/donations/checkout", payload, {
        params: { origin_url: originUrl },
      })
      .then((r) => r.data),
  checkoutStatus: (sessionId) =>
    client.get(`/donations/checkout/status/${encodeURIComponent(sessionId)}`).then((r) => r.data),
  myDonations: () => client.get("/donations/me").then((r) => r.data),

  // Referrals
  myReferral: () => client.get("/referral/me").then((r) => r.data),
  trackReferralClick: (code) =>
    client.post(`/referral/track/${encodeURIComponent(code)}`).then((r) => r.data),

  // Admin (all gated server-side; frontend gates via /auth/me.is_admin)
  adminCheck: () => client.get("/admin/check").then((r) => r.data),
  adminStats: () => client.get("/admin/stats").then((r) => r.data),
  adminUsers: (params) => client.get("/admin/users", { params }).then((r) => r.data),
  adminDonations: (params) => client.get("/admin/donations", { params }).then((r) => r.data),
  adminTopReferrers: (params) =>
    client.get("/admin/referrals/top", { params }).then((r) => r.data),
  adminListApiKeys: () => client.get("/admin/api-keys").then((r) => r.data),
  adminSetApiKey: (provider, apiKey) =>
    client
      .post(`/admin/api-keys/${encodeURIComponent(provider)}`, { api_key: apiKey })
      .then((r) => r.data),
  adminDeleteApiKey: (provider) =>
    client.delete(`/admin/api-keys/${encodeURIComponent(provider)}`).then((r) => r.data),
  adminWebhookErrors: (limit = 50) =>
    client.get("/admin/webhook-errors", { params: { limit } }).then((r) => r.data),
  adminRecentReferrals: (params) =>
    client.get("/admin/referrals/recent", { params }).then((r) => r.data),

  // ── Sprint B admin endpoints ──
  adminOverview: () => client.get("/admin/overview").then((r) => r.data),
  adminUsersV2: (params) =>
    client.get("/admin/users-v2", { params }).then((r) => r.data),
  adminDonors: (params) =>
    client.get("/admin/donors", { params }).then((r) => r.data),
  adminResendReceipt: (donationId) =>
    client
      .post(`/admin/donations/${encodeURIComponent(donationId)}/resend-receipt`)
      .then((r) => r.data),
  adminPromoters: (params) =>
    client.get("/admin/promoters", { params }).then((r) => r.data),
  adminPromoterReferrals: (userId, params) =>
    client
      .get(`/admin/promoters/${encodeURIComponent(userId)}/referrals`, { params })
      .then((r) => r.data),
  adminRecentActivity: (params) =>
    client.get("/admin/recent-activity", { params }).then((r) => r.data),
  adminChartGrowth: (params) =>
    client.get("/admin/charts/growth", { params }).then((r) => r.data),
  adminChartDonations: (params) =>
    client.get("/admin/charts/donations", { params }).then((r) => r.data),
  adminClickSources: (params) =>
    client.get("/admin/clicks/sources", { params }).then((r) => r.data),
  adminPromoterQR: (userId) =>
    `${BACKEND_URL}/api/admin/promoters/${encodeURIComponent(userId)}/qr-code`,
  adminDeleteUser: (userId) =>
    client.delete(`/admin/users/${encodeURIComponent(userId)}`).then((r) => r.data),
  adminCleanupDemoData: () =>
    client.post("/admin/cleanup/demo-data").then((r) => r.data),

  // ── Hebrew Calendar ──
  calendarMonth: (year, month) =>
    client.get("/calendar/month", { params: { year, month } }).then((r) => r.data),
  calendarToday: () => client.get("/calendar/today").then((r) => r.data),
  calendarZmanim: (date, locationOrParams) => {
    // Back-compat: a plain string is the catalog id (e.g. "jerusalem").
    // Otherwise we pass the descriptor's keys through as query params.
    const params = typeof locationOrParams === "string"
      ? { date, location: locationOrParams }
      : { date, ...(locationOrParams || {}) };
    return client.get("/calendar/zmanim", { params }).then((r) => r.data);
  },
  calendarLocations: () =>
    client.get("/calendar/zmanim/locations").then((r) => r.data),
  calendarMonthShabbatTimes: (year, month, locationOrParams) => {
    const base = { year, month };
    const extra = typeof locationOrParams === "string"
      ? { location: locationOrParams }
      : locationOrParams?.kind === "catalog"
      ? { location: locationOrParams.id }
      : locationOrParams?.kind === "custom" && locationOrParams.geonameid
      ? { geonameid: locationOrParams.geonameid }
      : locationOrParams?.kind === "custom" && locationOrParams.latitude != null
      ? {
          lat:  locationOrParams.latitude,
          lon:  locationOrParams.longitude,
          tzid: locationOrParams.tzid,
        }
      : { location: "jerusalem" };
    return client
      .get("/calendar/month-shabbat-times", { params: { ...base, ...extra } })
      .then((r) => r.data);
  },
  setMyLocation: (location) =>
    // `location` may be a catalog id string OR a descriptor object —
    // backend accepts both.
    client.put("/me/location", { location }).then((r) => r.data),
  searchLocations: (q) =>
    client.get("/calendar/locations/search", { params: { q } }).then((r) => r.data),
  reverseGeocode: (lat, lon) =>
    client.get("/calendar/locations/reverse", { params: { lat, lon } }).then((r) => r.data),
  getRecentLocations: () =>
    client.get("/me/recent-locations").then((r) => r.data),
  addRecentLocation: (loc) =>
    client.post("/me/recent-locations", loc).then((r) => r.data),

  // ── Personal calendar events ──
  myEventsList: (params) => client.get("/me/events", { params }).then((r) => r.data),
  myEventCreate: (payload) => client.post("/me/events", payload).then((r) => r.data),
  myEventUpdate: (id, payload) =>
    client.put(`/me/events/${encodeURIComponent(id)}`, payload).then((r) => r.data),
  myEventDelete: (id) =>
    client.delete(`/me/events/${encodeURIComponent(id)}`).then((r) => r.data),
};

// Book slug <-> URL helper
export const bookUrl = (slug) => encodeURIComponent(slug.replace(/ /g, "-"));
export const urlToSlug = (urlSlug) => decodeURIComponent(urlSlug).replace(/-/g, " ");

// Strip Hebrew vowels (nikud)
export function stripNikud(text) {
  if (!text) return "";
  // Remove Hebrew points & cantillation marks
  return text.replace(/[\u0591-\u05C7]/g, "");
}
